package at.theatervogelweide.admin.web

import at.theatervogelweide.admin.contract.Account
import at.theatervogelweide.admin.contract.AdminError
import at.theatervogelweide.admin.contract.AdminJson
import at.theatervogelweide.admin.contract.AdminResult
import at.theatervogelweide.admin.contract.Api
import at.theatervogelweide.admin.contract.ChangePasswordRequest
import at.theatervogelweide.admin.contract.IMAGE_PART
import at.theatervogelweide.admin.contract.ImageCategory
import at.theatervogelweide.admin.contract.PasswordLinkInfo
import at.theatervogelweide.admin.contract.PasswordLinkRequest
import at.theatervogelweide.admin.contract.REQUEST_HEADER
import at.theatervogelweide.admin.contract.REQUEST_HEADER_VALUE
import at.theatervogelweide.admin.contract.RPC_PATH
import at.theatervogelweide.admin.contract.SetPasswordRequest
import at.theatervogelweide.admin.contract.SignInRequest
import at.theatervogelweide.admin.contract.UploadedImage
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.plugins.contentnegotiation.ContentNegotiation
import io.ktor.client.plugins.defaultRequest
import io.ktor.client.plugins.resources.Resources
import io.ktor.client.plugins.resources.delete
import io.ktor.client.plugins.resources.get
import io.ktor.client.plugins.resources.post
import io.ktor.client.plugins.resources.put
import io.ktor.client.plugins.websocket.WebSockets
import io.ktor.client.request.header
import io.ktor.client.request.setBody
import io.ktor.client.statement.HttpResponse
import io.ktor.http.ContentType
import io.ktor.http.HttpStatusCode
import io.ktor.http.contentType
import io.ktor.http.isSuccess
import io.ktor.serialization.kotlinx.json.json
import io.ktor.resources.href
import io.ktor.resources.serialization.ResourcesFormat
import kotlinx.browser.window
import kotlinx.coroutines.await
import org.w3c.fetch.RequestInit
import org.w3c.files.File
import org.w3c.xhr.FormData
import kotlinx.rpc.krpc.ktor.client.KtorRpcClient
import kotlinx.rpc.krpc.ktor.client.installKrpc
import kotlinx.rpc.krpc.ktor.client.rpc
import kotlinx.rpc.krpc.ktor.client.rpcConfig
import kotlinx.rpc.krpc.serialization.json.json

/** The plain HTTP part of the API (#11), on the page's own origin. */
class AdminApi {
    private val resources = ResourcesFormat()

    private val client = HttpClient {
        expectSuccess = false
        install(ContentNegotiation) { json(AdminJson) }
        install(Resources)
        install(WebSockets)
        installKrpc()
        defaultRequest {
            url(window.location.origin + "/")
            // the server rejects changing requests without it (#16); it costs nothing on the others
            header(REQUEST_HEADER, REQUEST_HEADER_VALUE)
        }
    }

    /** The signed-in account, or `null` without a valid session. */
    suspend fun currentAccount(): Account? {
        val response = client.get(Api.Session())
        return when {
            response.status == HttpStatusCode.Unauthorized -> null
            else -> response.result<Account>().orThrow()
        }
    }

    suspend fun signIn(request: SignInRequest): AdminResult<Account> = client.post(Api.Session()) {
        contentType(ContentType.Application.Json)
        setBody(request)
    }.result()

    suspend fun signOut() {
        client.delete(Api.Session()).result<Unit>().orThrow()
    }

    /** "Passwort ändern" (#66); this session goes on, the account's others end. */
    suspend fun changePassword(request: ChangePasswordRequest): AdminResult<Account> = client.put(Api.Session.Password()) {
        contentType(ContentType.Application.Json)
        setBody(request)
    }.result()

    /** What the one-time link with [token] is for, or why it can't be used. */
    suspend fun checkPasswordLink(token: String): AdminResult<PasswordLinkInfo> = client.post(Api.PasswordLink()) {
        contentType(ContentType.Application.Json)
        setBody(PasswordLinkRequest(token))
    }.result()

    /** Sets the password with a one-time link; answers with the signed-in account, or `null` if it can't sign in here. */
    suspend fun setPasswordWithLink(request: SetPasswordRequest): AdminResult<Account?> {
        val response = client.post(Api.PasswordLink.Password()) {
            contentType(ContentType.Application.Json)
            setBody(request)
        }
        return if (response.status == HttpStatusCode.NoContent) AdminResult.Success(null) else response.result<Account>()
    }

    /**
     * Uploads one image (#11). It goes through `fetch` with a `FormData` body rather than the Ktor client, so the browser
     * sends the multipart length up front, which the server needs.
     */
    suspend fun uploadImage(category: ImageCategory, file: File): AdminResult<UploadedImage> {
        val form = FormData()
        form.append(IMAGE_PART, file, file.name)
        val response = window.fetch(
            href(resources, Api.Images(category = category)),
            // Kotlin's RequestInit() sets every option it doesn't get to null, which fetch refuses
            kotlin.js.json(
                "method" to "POST",
                "headers" to kotlin.js.json(REQUEST_HEADER to REQUEST_HEADER_VALUE),
                "body" to form,
                "credentials" to "same-origin",
            ).unsafeCast<RequestInit>(),
        ).await()
        val body = response.text().await()

        return if (response.ok) {
            AdminResult.Success(AdminJson.decodeFromString(UploadedImage.serializer(), body))
        } else {
            AdminResult.Failure(runCatching { AdminJson.decodeFromString(AdminError.serializer(), body) }.getOrElse { AdminError.internal() })
        }
    }

    /** Where an uploaded image can be shown, e.g. in an `<img>`. */
    fun imageUrl(category: ImageCategory, uuid: String): String =
        href(resources, Api.Images.Image(Api.Images(category = category), uuid))

    /** The kotlinx.rpc connection; the browser sends the session cookie and the page's origin with the upgrade. */
    fun rpc(): KtorRpcClient = client.rpc(rpcUrl(window.location.protocol, window.location.host)) {
        rpcConfig {
            serialization {
                json(AdminJson)
            }
        }
    }

    private suspend inline fun <reified T> HttpResponse.result(): AdminResult<T> = when {
        status.isSuccess() -> AdminResult.Success(if (T::class == Unit::class) Unit as T else body<T>())
        else -> AdminResult.Failure(runCatching { body<AdminError>() }.getOrElse { AdminError.internal() })
    }
}

/**
 * The RPC WebSocket on the page's own origin. [host] carries the port only when it isn't the default one, so the
 * address stays the page's origin, which the server's `Origin` check and the `connect-src 'self'` policy both expect.
 */
fun rpcUrl(pageProtocol: String, host: String): String =
    "${if (pageProtocol == "https:") "wss" else "ws"}://$host$RPC_PATH"

class AdminException(val error: AdminError) : Exception(error.message)

fun <T> AdminResult<T>.orThrow(): T = when (this) {
    is AdminResult.Success -> value
    is AdminResult.Failure -> throw AdminException(error)
}
